import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { Award, BadgeCheck } from 'lucide-react-native';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { Text } from '@/components';
import type { Collectible } from '@/lib/api/account';
import { useTranslation } from 'react-i18next';

// The collectibles API has no earned/locked field — the state is encoded in
// the asset filename. Live data (verified via curl): locked assets carry a
// "Grey" marker (e.g. "Bookmark_Grey_poster.webp" / "Bookmark_Grey.glb");
// earned ones don't. "-true"/"-false" suffixes supported as fallback.
// ponytail: substring match on the basename; switch to a real API field when
// the backend exposes one.
export function collectibleEarned(c: Collectible): boolean {
  const file = (c.preview_url || c.image_url || '').split('/').pop()?.toLowerCase() ?? '';
  if (file.includes('-false.') || file.includes('grey')) return false;
  if (file.includes('-true.') || file.includes('earned')) return true;
  return true; // no marker in the name → treat as earned
}
export default function AwardsGrid({ collectibles }: { collectibles: Collectible[] }) {
  const { t } = useTranslation();
  return (
    <View style={styles.grid}>
      {collectibles.map((c, i) => {
        const earned = collectibleEarned(c);
        return (
          <View key={`${c.name}-${i}`} style={[styles.tile, !earned && styles.tileLocked]}>
            <View style={styles.tileArt}>
              {c.preview_url ? (
                <Image source={{ uri: c.preview_url }} style={[styles.tileImage, !earned && styles.tileImageLocked]} contentFit="cover" cachePolicy="memory-disk" />
              ) : (
                <Award size={28} color={theme.textMuted} />
              )}
            </View>
            <Text style={[styles.tileName, !earned && styles.tileNameLocked]} numberOfLines={1}>{c.name}</Text>
            {earned && (
              <View style={styles.tileEarnedRow}>
                <BadgeCheck size={12} color={theme.textMuted} />
                <Text style={styles.tileEarnedText}>{t('profile.earned')}</Text>
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  tile: {
    width: '31.5%',
    alignItems: 'center',
    gap: 12,
  },
  tileArt: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  tileImage: {
    width: '100%',
    height: '100%',
  },
  tileName: {
    ...Typography.body(theme.textPrimary),
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  tileEarnedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  tileEarnedText: {
    ...Typography.caption(theme.textMuted),
    fontSize: 10,
    fontWeight: '500',
  },
  tileLocked: {
    opacity: 0.4,
  },
  tileImageLocked: {
    opacity: 0.6,
  },
  tileNameLocked: {
    color: theme.textMuted,
  },
});